/**
 * AddressAutocomplete Component
 * 
 * Google Places Address Autocomplete for GLP-1 intake flow.
 * Provides address suggestions as user types, mobile-optimized.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react'
import { MapPin, Loader2, X } from 'lucide-react'

// Styles for Google Places autocomplete dropdown - fixed positioning
const autocompleteStyles = `
  .pac-container {
    background-color: #ffffff !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 12px !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3) !important;
    font-family: inherit !important;
    z-index: 100000 !important;
    width: auto !important;
    min-width: 300px !important;
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
  }
  .pac-container.pac-container-positioned {
    opacity: 1;
  }
  .pac-item {
    padding: 12px 16px !important;
    cursor: pointer !important;
    border-top: 1px solid #f3f4f6 !important;
    font-size: 14px !important;
    color: #1f2937 !important;
    line-height: 1.4 !important;
  }
  .pac-item:first-child {
    border-top: none !important;
    border-radius: 12px 12px 0 0 !important;
  }
  .pac-item:last-child {
    border-radius: 0 0 12px 12px !important;
  }
  .pac-item:hover {
    background-color: #f3f4f6 !important;
  }
  .pac-item-selected {
    background-color: #e0f2fe !important;
  }
  .pac-icon {
    display: none !important;
  }
  .pac-item-query {
    font-weight: 600 !important;
    color: #4A8A9A !important;
  }
  .pac-matched {
    font-weight: 600 !important;
    color: #4A8A9A !important;
  }
`

interface AddressComponents {
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
  country: string
}

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onAddressSelect: (components: AddressComponents) => void
  placeholder?: string
  disabled?: boolean
}

// Google Maps API types
declare global {
  interface Window {
    google: any
    initGooglePlaces: () => void
  }
}

// Use same API key as AddressInput component - with fallback for development
const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_PLACES_API_KEY || 'AIzaSyBvyO6cd1Wd5UmTKfqv-9Nl92SDDFa4gGU'

export default function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = '123 Main Street',
  disabled = false,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const repositionIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Keep stable refs for callbacks so the place_changed listener always uses the latest versions
  const onChangeRef = useRef(onChange)
  const onAddressSelectRef = useRef(onAddressSelect)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])
  useEffect(() => { onAddressSelectRef.current = onAddressSelect }, [onAddressSelect])

  // Inject autocomplete styles
  useEffect(() => {
    const styleId = 'google-places-autocomplete-styles'
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement('style')
      styleElement.id = styleId
      styleElement.textContent = autocompleteStyles
      document.head.appendChild(styleElement)
    }
  }, [])

  // Reposition pac-container - runs continuously while input is focused
  const repositionDropdown = useCallback(() => {
    if (!inputRef.current) return

    const pacContainers = document.querySelectorAll('.pac-container')
    if (pacContainers.length === 0) return

    const inputRect = inputRef.current.getBoundingClientRect()
    
    // Find the visible pac-container (the one with display != none)
    pacContainers.forEach((container) => {
      const pacContainer = container as HTMLElement
      const computedStyle = window.getComputedStyle(pacContainer)
      
      if (computedStyle.display !== 'none') {
        // Position the container
        pacContainer.style.setProperty('position', 'fixed', 'important')
        pacContainer.style.setProperty('top', `${inputRect.bottom + 4}px`, 'important')
        pacContainer.style.setProperty('left', `${inputRect.left}px`, 'important')
        pacContainer.style.setProperty('width', `${inputRect.width}px`, 'important')
        pacContainer.style.setProperty('z-index', '100000', 'important')
        
        // Show the container after positioning (prevents flicker)
        if (!pacContainer.classList.contains('pac-container-positioned')) {
          pacContainer.classList.add('pac-container-positioned')
        }
      }
    })
  }, [])

  // Start repositioning when input is focused, stop when blurred
  const handleFocus = useCallback(() => {
    // Clear any existing interval
    if (repositionIntervalRef.current) {
      clearInterval(repositionIntervalRef.current)
    }
    // Reposition immediately and then every 50ms while focused
    repositionDropdown()
    repositionIntervalRef.current = setInterval(repositionDropdown, 50)
  }, [repositionDropdown])

  const handleBlur = useCallback(() => {
    // Stop repositioning after a short delay (allow click on dropdown)
    setTimeout(() => {
      if (repositionIntervalRef.current) {
        clearInterval(repositionIntervalRef.current)
        repositionIntervalRef.current = null
      }
    }, 300)
  }, [])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (repositionIntervalRef.current) {
        clearInterval(repositionIntervalRef.current)
      }
    }
  }, [])

  // Load Google Places script
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsScriptLoaded(true)
      return
    }

    if (!GOOGLE_PLACES_API_KEY) {
      console.warn('Google Places API key not configured')
      return
    }

    // Check if script is already loading
    const existingScript = document.getElementById('google-places-script')
    if (existingScript) {
      existingScript.addEventListener('load', () => setIsScriptLoaded(true))
      return
    }

    const script = document.createElement('script')
    script.id = 'google-places-script'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => setIsScriptLoaded(true)
    script.onerror = () => console.error('Failed to load Google Places API')
    document.head.appendChild(script)

    return () => {
      // Cleanup if needed
    }
  }, [])

  // Initialize autocomplete
  useEffect(() => {
    if (!isScriptLoaded || !inputRef.current || autocompleteRef.current) return

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: 'us' },
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry'],
        }
      )

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace()
        if (!place?.address_components) return

        const components = parseAddressComponents(place.address_components)

        // Use refs to always call the latest callbacks (avoids stale closures)
        onChangeRef.current(components.addressLine1)
        onAddressSelectRef.current(components)
      })
    } catch (error) {
      console.error('Failed to initialize Google Places Autocomplete:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded])

  // Parse Google address components into our format
  const parseAddressComponents = useCallback((components: any[]): AddressComponents => {
    const result: AddressComponents = {
      addressLine1: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    }

    let streetNumber = ''
    let route = ''

    components.forEach((component: any) => {
      const types = component.types

      if (types.includes('street_number')) {
        streetNumber = component.long_name
      }
      if (types.includes('route')) {
        route = component.long_name
      }
      if (types.includes('locality') || types.includes('sublocality')) {
        result.city = component.long_name
      }
      if (types.includes('administrative_area_level_1')) {
        result.state = component.short_name // Use abbreviation (e.g., "TX")
      }
      if (types.includes('postal_code')) {
        result.postalCode = component.long_name
      }
      if (types.includes('country')) {
        result.country = component.short_name
      }
    })

    // Combine street number and route
    result.addressLine1 = [streetNumber, route].filter(Boolean).join(' ')

    return result
  }, [])

  const handleClear = () => {
    onChange('')
    onAddressSelect({
      addressLine1: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    })
    inputRef.current?.focus()
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MapPin
          size={18}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
          style={{ color: '#4A8A9A' }}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled || isLoading}
          className="w-full bg-transparent rounded-[14px] min-h-[56px] pl-10 pr-10 py-2 text-white placeholder-white/50 border border-white transition-all duration-200 font-medium focus:outline-none focus:ring-0 focus:border-[#4A8A9A]"
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-gray-700 transition-colors"
            style={{ color: '#C1C9D2' }}
          >
            <X size={16} />
          </button>
        )}
        {isLoading && (
          <Loader2
            size={18}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 animate-spin"
            style={{ color: '#2FFFC7' }}
          />
        )}
      </div>
      
      {!GOOGLE_PLACES_API_KEY && (
        <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
          Address autocomplete not configured
        </p>
      )}
    </div>
  )
}
